import { useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import { useAccount, useFeeData } from 'wagmi';
import BigNumber from 'bignumber.js';
import { useTokenBalance } from 'src/api/smart-contracts';
import { buildQueryParams } from 'src/utils';
import { TradeType, Quote } from 'src/types';

// Monad network constants
const MONAD_MIN_BASE_FEE = 100n; // 100 MON-gwei (100 * 10^-9 MON)
const GWEI = 1_000_000_000n; // 1 gwei in wei

const QUOTE_API_URL = import.meta.env.VITE_DEX_QUOTE_API_URL;

export function usePrepareSwap({
  amount,
  tokenIn,
  tokenOut,
  tradeType,
  slippage = 'auto',
  deadlineValue,
}: {
  amount?: string;
  tokenIn?: string;
  tokenOut?: string;
  tradeType: TradeType;
  slippage?: string;
  deadlineValue?: string;
}) {
  const { address } = useAccount();

  const queryParamsStr = buildQueryParams({
    //protocols: 'v2',
    //tokenInChainId: CHAIN_ID,
    // tokenOutChainId: CHAIN_ID,
    //tokenInAddress: tokenIn,
    inputMint: tokenIn,
    //tokenOutAddress: tokenOut,
    outputMint: tokenOut,
    amount,
    //type: tradeType === 'EXACT_INPUT' ? 'exactIn' : 'exactOut',
    //...(slippage !== 'auto' && { slippageTolerance: slippage }),
    ...(slippage === 'auto'
      ? { dynamicSlippage: true }
      : { slippageBps: BigNumber(slippage).times(100).toString() }),
    //deadline: deadlineValue,
    //enableDebug: 'true',
    ...(!!address && { swapper: address }),
  });
  const quoteUrl = `${QUOTE_API_URL}/quote?${queryParamsStr}`;

  const [debouncedAmount] = useDebouncedValue(amount, 300);

  const { balance: MONbalance } = useTokenBalance();

  const { balance: tokenInBalance } = useTokenBalance({
    tokenAddress: tokenIn,
    enabled: !!tokenIn,
  });

  // Get EIP-1559 fee data for accurate gas cost calculation
  const { data: feeData } = useFeeData({
    query: {
      // Refetch every block to get current base fee
      refetchInterval: 12000, // ~12 seconds (Monad block time)
    },
  });

  const {
    data: quote,
    isLoading: isLoadingQuote,
    error: quoteError,
  } = useQuery({
    queryKey: [
      'quote',
      debouncedAmount,
      tokenIn,
      tokenOut,
      tradeType,
      slippage,
    ],
    queryFn: async (): Promise<Quote> => {
      try {
        const response = await fetch(quoteUrl);
        const json = await response.json();

        if (json.error) {
          throw new Error(json.error);
        }

        if (json.errorCode) {
          throw new Error(json.detail);
        }

        return json;
      } catch (e) {
        throw e instanceof Error ? e : new Error(String(e));
      }
    },
    refetchInterval: 1000 * 10,
    enabled:
      BigNumber(amount ?? 0).gt(0) &&
      !!debouncedAmount &&
      BigNumber(debouncedAmount).gt(0) &&
      !!tokenIn &&
      !!tokenOut,
  });

  const gasEstimate =
    quote?.swapDetails?.calls?.reduce((acc, c) => {
      return acc.plus(c?.gasLimit ?? '0');
    }, BigNumber(0)) || BigNumber(0);

  // Calculate transaction cost using EIP-1559 pricing
  // According to Monad docs: price_per_gas = min(base_fee + priority_fee, max_fee_per_gas)
  let transactionCost = BigNumber(0);

  if (feeData && gasEstimate.gt(0)) {
    // Use maxFeePerGas if available (recommended total fee)
    // Otherwise calculate base + priority
    let pricePerGas: bigint;

    if (feeData.maxFeePerGas) {
      pricePerGas = feeData.maxFeePerGas;
    } else if (feeData.gasPrice && feeData.maxPriorityFeePerGas) {
      pricePerGas = feeData.gasPrice + feeData.maxPriorityFeePerGas;
    } else if (feeData.gasPrice) {
      pricePerGas = feeData.gasPrice;
    } else {
      pricePerGas = MONAD_MIN_BASE_FEE * GWEI;
    }

    // Ensure minimum base fee (100 MON-gwei)
    const minPricePerGas = MONAD_MIN_BASE_FEE * GWEI;
    pricePerGas = pricePerGas < minPricePerGas ? minPricePerGas : pricePerGas;

    transactionCost = gasEstimate.times(String(pricePerGas));
  } else if (gasEstimate.gt(0)) {
    // Fallback: use minimum base fee if fee data not available
    const fallbackPricePerGas = MONAD_MIN_BASE_FEE * GWEI;
    transactionCost = gasEstimate.times(String(fallbackPricePerGas));
  }

  const MONbalanceAmount = String(MONbalance?.value || 0);
  const notEnoughBalanceForGas = transactionCost.gte(MONbalanceAmount);

  return {
    quote,
    tokenInBalance,
    isLoadingQuote,
    quoteError,
    transactionCost: transactionCost.div(1e18),
    notEnoughBalanceForGas,
  };
}
